import type { AvatarStyle } from '@chicare/shared';

import { cn } from '@/lib/cn';

/**
 * A buyer's face, however they chose to have one.
 *
 * ── THREE STATES, IN THE ORDER THEY WERE CHOSEN ─────────────────────────────
 *
 *   photo      one they uploaded
 *   portrait   one they tapped from a row of drawings
 *   initials   neither, which is the default and is fine
 *
 * ── AND WHY THERE IS NO GREY SILHOUETTE ─────────────────────────────────────
 *
 * The fallback is the buyer's own initials on brand blue, not the generic
 * head-and-shoulders every form on the internet uses. That shape reads as
 * "we don't know who you are"; two letters of somebody's actual name reads as
 * "this is your account", which is what this screen is for — and it costs
 * nothing, needs no upload and assumes nothing about anybody.
 *
 * ── THE PORTRAITS ARE DRAWN HERE, NOT FETCHED ───────────────────────────────
 *
 * Inline SVG rather than image files: they are a few hundred bytes each, they
 * take the brand colour from CSS rather than baking it in, and they render
 * instantly on a connection where a second round trip for a decorative picture
 * is a second chance to show nothing at all.
 */
export function Avatar({
  name,
  initials,
  photoUrl,
  style,
  size = 56,
  className,
}: {
  name: string;
  initials: string;
  photoUrl?: string | null;
  style?: AvatarStyle | null;
  size?: number;
  className?: string;
}) {
  const shell = cn(
    'shrink-0 overflow-hidden rounded-full bg-blue-deep ring-2 ring-white/70',
    className,
  );

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        // Their name, so a screen reader says whose face it is. Not "avatar".
        alt={name}
        width={size}
        height={size}
        className={cn(shell, 'object-cover')}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={cn(shell, 'grid place-items-center')}
      style={{ width: size, height: size }}
      // Decorative in both remaining cases: the name is printed beside it, and
      // hearing "E M" or "a drawing of a person" read out first is noise.
      aria-hidden
    >
      {style ? (
        <Portrait style={style} size={size} />
      ) : (
        <span
          className="font-display font-bold leading-none text-white"
          style={{ fontSize: Math.round(size * 0.36) }}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

/**
 * The drawn portraits.
 *
 * ── DRAWN FOR 40 PIXELS, NOT FOR 400 ────────────────────────────────────────
 *
 * The first version of these differed only in a pale wash of hair at 55%
 * opacity, which was legible on a design surface and completely invisible in
 * the picker — three identical blue discs, presented as a choice. So the hair
 * is now a solid mid-tone that contrasts with BOTH the near-white face and the
 * deep blue behind it, and the silhouettes differ in outline rather than in
 * shading: long hair past the jaw, a cropped cap, or nothing at all.
 *
 * Flat and simple on purpose. They are a way to have a face without uploading
 * one, not a likeness — and a more detailed drawing would only be more
 * obviously not you.
 */
export function Portrait({ style, size = 56 }: { style: AvatarStyle; size?: number }) {
  // Three tones, far enough apart to survive a cheap screen at 40px.
  const skin = '#E9EFFA';
  const hair = '#89A9E2';

  return (
    <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden className="block">
      <circle cx="24" cy="24" r="24" fill="var(--color-blue-deep)" />

      {/* Long hair goes BEHIND the head, so the face sits in front of it. */}
      {style === 'woman' ? (
        <path
          d="M24 8.5c6.6 0 11.5 4.8 11.5 11.4v11.4c0 1.6-1.3 2.9-2.9 2.9h-1.7V19.9a6.9 6.9 0 0 0-13.8 0v14.3h-1.7a2.9 2.9 0 0 1-2.9-2.9V19.9C12.5 13.3 17.4 8.5 24 8.5Z"
          fill={hair}
        />
      ) : null}

      {/* Shoulders. */}
      <path
        d="M24 27.5c8.1 0 14.9 5.5 15.8 12.6A24 24 0 0 1 24 48a24 24 0 0 1-15.8-7.9C9.1 33 15.9 27.5 24 27.5Z"
        fill={skin}
      />

      {/* Head. */}
      <circle cx="24" cy="20" r="8.4" fill={skin} />

      {style === 'woman' ? (
        // A fringe across the brow, sitting on top of the face.
        <path
          d="M15.7 18.9c.5-4.6 4-8 8.3-8s7.8 3.4 8.3 8c-2.2-2.5-4.9-3.8-8.3-3.8s-6.1 1.3-8.3 3.8Z"
          fill={hair}
        />
      ) : style === 'man' ? (
        // A cropped cap, clearly on top of the head rather than around it.
        <path
          d="M16 18c.4-4.2 3.8-7.2 8-7.2s7.6 3 8 7.2c-1.9-1.4-4.5-2.2-8-2.2s-6.1.8-8 2.2Z"
          fill={hair}
        />
      ) : null}
    </svg>
  );
}
